// AGC: ManageCategoriesUnitsModal – list/table to add, edit, remove custom categories or units
// AGC: Used from Register new product form (+ next to category/unit). Built-in items are read-only.

import React, { useState } from 'react'
import { Plus, Pencil, Trash2, X } from 'lucide-react'

// AGC: Single custom item (category or unit)
// AGC: stockTreatment controls how transactions in this category affect inventory:
// AGC:   stock   = adds/removes from stock quantity
// AGC:   asset   = creates/disposes a long-term asset (machine, land)
// AGC:   expense = money record only, no stock or asset movement
// AGC:   ask     = user decides per transaction via toggle
export interface CustomListItem {
  id: string
  label: string
  stockTreatment?: 'stock' | 'asset' | 'expense' | 'ask'
}

export interface ManageCategoriesUnitsModalProps {
  isOpen: boolean
  onClose: () => void
  /** 'categories' | 'units' */
  mode: 'categories' | 'units'
  /** Built-in items (read-only in table) */
  builtInItems: CustomListItem[]
  /** User-added custom items (editable/removable) */
  customItems: CustomListItem[]
  onAdd: (item: CustomListItem) => void
  onEdit: (oldId: string, item: CustomListItem) => void
  onRemove: (id: string) => void
  /** Translation helper */
  t: (key: string) => string
}

// AGC: Slug from label for new item id (unique-ified by caller if needed)
export function slugFromLabel(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_àáâãäåèéêëìíîïòóôõöùúûüç]/gi, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || 'custom'
}

export function ManageCategoriesUnitsModal({
  isOpen,
  onClose,
  mode,
  builtInItems,
  customItems,
  onAdd,
  onEdit,
  onRemove,
  t,
}: ManageCategoriesUnitsModalProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [newLabel, setNewLabel] = useState('')

  const title =
    mode === 'categories'
      ? t('inventory.manageCategoriesUnits.titleCategories') || 'Manage categories'
      : t('inventory.manageCategoriesUnits.titleUnits') || 'Manage units'

  const handleStartAdd = () => {
    setIsAdding(true)
    setNewLabel('')
  }
  const handleSaveAdd = () => {
    const label = newLabel.trim()
    if (!label) return
    const id = slugFromLabel(label)
    const baseId = id
    let finalId = baseId
    let n = 0
    const allIds = new Set([...builtInItems.map((i) => i.id), ...customItems.map((i) => i.id)])
    while (allIds.has(finalId)) {
      n += 1
      finalId = `${baseId}_${n}`
    }
    onAdd({ id: finalId, label })
    setIsAdding(false)
    setNewLabel('')
  }
  const handleCancelAdd = () => {
    setIsAdding(false)
    setNewLabel('')
  }

  const handleStartEdit = (item: CustomListItem) => {
    setEditingId(item.id)
    setEditLabel(item.label)
  }
  const handleSaveEdit = () => {
    if (editingId == null) return
    const label = editLabel.trim()
    if (!label) return
    // AGC: Keep same id when editing; only label changes
    onEdit(editingId, { id: editingId, label })
    setEditingId(null)
    setEditLabel('')
  }
  const handleCancelEdit = () => {
    setEditingId(null)
    setEditLabel('')
  }

  const allItems = [...builtInItems, ...customItems]
  const isBuiltIn = (id: string) => builtInItems.some((i) => i.id === id)

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 w-full max-w-lg rounded-2xl shadow-xl overflow-hidden max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500"
            aria-label={t('common.close') || 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-auto flex-1">
          {/* AGC: Add new row (inline form) */}
          {isAdding && (
            <div className="flex items-center gap-2 mb-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={
                  mode === 'categories'
                    ? t('inventory.manageCategoriesUnits.categoryNamePlaceholder') || 'Category name'
                    : t('inventory.manageCategoriesUnits.unitNamePlaceholder') || 'Unit (e.g. L, kg)'
                }
                className="flex-1 px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveAdd()
                  if (e.key === 'Escape') handleCancelAdd()
                }}
              />
              <button
                type="button"
                onClick={handleSaveAdd}
                disabled={!newLabel.trim()}
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
              >
                {t('common.save') || 'Save'}
              </button>
              <button
                type="button"
                onClick={handleCancelAdd}
                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 text-sm"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          )}

          {/* AGC: Table / list */}
          <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-700/80">
                  <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                    {mode === 'categories'
                      ? t('inventory.manageCategoriesUnits.categoryName') || 'Category'
                      : t('inventory.manageCategoriesUnits.unitName') || 'Unit'}
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-gray-700 dark:text-gray-300 w-24">
                    {t('inventory.manageCategoriesUnits.actions') || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {allItems.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-gray-100 dark:border-slate-600/80 hover:bg-gray-50/50 dark:hover:bg-slate-700/30"
                  >
                    <td className="py-2 px-3 text-gray-900 dark:text-white">
                      {editingId === item.id ? (
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="w-full px-2 py-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded text-gray-900 dark:text-white"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit()
                            if (e.key === 'Escape') handleCancelEdit()
                          }}
                        />
                      ) : (
                        item.label
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {editingId === item.id ? (
                        <span className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                            aria-label={t('common.save')}
                          >
                            <CheckIcon />
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-600"
                            aria-label={t('common.cancel')}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ) : isBuiltIn(item.id) ? (
                        <span className="text-gray-400 dark:text-slate-500 text-xs">
                          {t('inventory.manageCategoriesUnits.builtIn') || 'Built-in'}
                        </span>
                      ) : (
                        <span className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => handleStartEdit(item)}
                            className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-600"
                            aria-label={t('common.edit') || 'Edit'}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onRemove(item.id)}
                            className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            aria-label={t('common.delete') || 'Remove'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex justify-between items-center shrink-0">
          <button
            type="button"
            onClick={handleStartAdd}
            disabled={isAdding}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-600 text-emerald-600 dark:border-emerald-500 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 text-sm font-medium disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {t('inventory.manageCategoriesUnits.addOne') || 'Add one'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 text-sm font-medium"
          >
            {t('common.close') || 'Close'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}
